import { useState, useRef, useEffect, useCallback } from "react";
import gsap from "gsap";
import {
  CheckCircleIcon,
  ExclamationTriangleIcon,
  EllipsisHorizontalIcon,
  HashtagIcon,
} from "@heroicons/react/24/outline";

// Feature imports.
// Camera / Archive / Detection / Editor Sends / AI Quality / Training / System
// tab components are still on disk under features/statistics/components/ but
// are not rendered here — tabs trimmed 2026-04-22 pending rebuild. See vault
// note "TODO — Statistics Tabs Rebuild".
import {
  AnimatedNumber,
  EditorThroughputTab,
  ImageSearch,
  PipelinePerformance,
  PipelineTimelineV2,
} from "../features/statistics/components";
import { fetchStatsOverview } from "../api";
import { useStatisticsData } from "../features/statistics/hooks";
import { STATS_TABS } from "../features/statistics/constants";
import { formatMs } from "../features/statistics/utils";
import { useSetTopBar } from "../layouts/ShellContext";
import { TabBar } from "../shared/components/redesign";

// localStorage keys for persisted Statistics filters. Versioned suffix
// so future schema changes don't fight with old values left in
// browsers.
const STATS_RANGE_KEY = "tank_stats_range_v1";
const STATS_ARCHIVED_KEY = "tank_stats_include_archived_v1";
const STATS_RANGE_DEFAULT = "24h";
const STATS_ARCHIVED_DEFAULT = true;

function readStoredRange() {
  try {
    const v = localStorage.getItem(STATS_RANGE_KEY);
    return v || STATS_RANGE_DEFAULT;
  } catch {
    return STATS_RANGE_DEFAULT;
  }
}

function readStoredArchived() {
  try {
    const v = localStorage.getItem(STATS_ARCHIVED_KEY);
    if (v === null) return STATS_ARCHIVED_DEFAULT;
    return v === "true";
  } catch {
    return STATS_ARCHIVED_DEFAULT;
  }
}

export default function Statistics({ onNavigate, onTopBadge }) {
  const [activeTab, setActiveTab] = useState("throughput");
  const fromDate = null;
  const toDate = null;
  // Range + archive toggle persist across navigations via localStorage.
  // Default: 24h + Live+Archive (per user spec) so a fresh-tab open
  // surfaces the most recent activity including just-deleted photos.
  // Range options align across tabs: 24h / 7d / 30d / 90d / 1y / all.
  const [statsRange, setStatsRange] = useState(readStoredRange);
  const [includeArchived, setIncludeArchived] = useState(readStoredArchived);

  useEffect(() => {
    try {
      localStorage.setItem(STATS_RANGE_KEY, statsRange);
    } catch {
      /* localStorage may be disabled (private mode); silently no-op */
    }
  }, [statsRange]);

  useEffect(() => {
    try {
      localStorage.setItem(STATS_ARCHIVED_KEY, String(includeArchived));
    } catch {
      /* localStorage may be disabled; silently no-op */
    }
  }, [includeArchived]);

  // Refs for GSAP animations
  const summaryCardsRef = useRef([]);
  const summaryAnimDone = useRef(false);

  // Reflect the active stats tab in the top-bar breadcrumb.
  const setTopBar = useSetTopBar();
  useEffect(() => {
    const labels = {
      throughput: 'Throughput',
      pipeline:   'Pipeline',
      timeline:   'Timeline',
    };
    setTopBar({ breadcrumb: labels[activeTab] || null });
    return () => setTopBar(null);
  }, [activeTab, setTopBar]);

  // Load all statistics data via hook. Kept for selectedImage state +
  // legacy data paths the new endpoint hasn't subsumed yet.
  const {
    stats,
    rate,
    loading,
    selectedImage,
    setSelectedImage,
  } = useStatisticsData({ activeTab, fromDate, toDate, onTopBadge });

  // Range-aware overview — KPI strip + Throughput sparkline + status
  // mix all read from /api/stats/overview/. Single source of truth for
  // counts that need to span live + archived photos.
  const [overview, setOverview] = useState(null);
  useEffect(() => {
    let cancelled = false;
    fetchStatsOverview(statsRange, includeArchived)
      .then((d) => {
        if (!cancelled) setOverview(d);
      })
      .catch(() => {
        if (!cancelled) setOverview(null);
      });
    return () => {
      cancelled = true;
    };
  }, [statsRange, includeArchived]);

  // KPI strip values — prefer the range-aware overview; fall back to
  // legacy-aggregated `stats` when the new endpoint isn't reachable
  // (e.g. backend not yet deployed).
  const kpiTotalImages = overview?.kpis?.total_images ?? stats?.total ?? 0;
  const kpiCompleted = overview?.kpis?.completed ?? stats?.done ?? 0;
  const kpiProcessing = overview?.kpis?.processing ?? stats?.processing ?? 0;
  const kpiAttention = overview?.kpis?.attention ?? stats?.attention ?? 0;
  // Numbers can be 0 — only fall through to the legacy rate when the
  // overview value is genuinely missing. `??` (not `||`) preserves zeros.
  const kpiImagesPerMin =
    overview?.kpis?.images_per_min ?? rate?.per_min_5m ?? rate?.per_min ?? null;
  const kpiAvgPipelineSec = overview?.kpis?.avg_pipeline_seconds ?? null;

  // Reusable GSAP animation for card grids
  const animateCards = useCallback((refs) => {
    const elements = refs.current?.filter(Boolean);
    if (!elements?.length) return;
    gsap.fromTo(
      elements,
      { opacity: 0, y: 20 },
      { opacity: 1, y: 0, duration: 0.4, stagger: 0.05, ease: "power2.out" }
    );
  }, []);

  // Animate summary cards on initial load only
  useEffect(() => {
    if (!loading && stats && !summaryAnimDone.current) {
      animateCards(summaryCardsRef);
      summaryAnimDone.current = true;
    }
  }, [loading, stats, animateCards]);

  if (loading) {
    return (
      <div className="page page-statistics">
        <div className="stats-loading">
          <div className="loading-matrix">
            <span>L</span>
            <span>O</span>
            <span>A</span>
            <span>D</span>
            <span>I</span>
            <span>N</span>
            <span>G</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="page page-statistics">
      {/* Header — route + breadcrumb live in TankShell's TopBar. We
          render the tab strip + a single shared range picker. The
          picker sits on the right edge of the header (same pattern
          across tabs) and governs all three: Throughput sparkline,
          Performance chart, KPI strip. The Timeline tab is range-
          irrelevant (per-photo) so the picker is hidden there. */}
      <div className="stats-header">
        <TabBar
          active={activeTab}
          onChange={setActiveTab}
          tabs={STATS_TABS.map((tab) => ({ id: tab.id, label: tab.label }))}
        />
        {activeTab !== "timeline" && (
          <div className="stats-range">
            <TabBar
              active={includeArchived ? "archive" : "live"}
              onChange={(id) => setIncludeArchived(id === "archive")}
              tabs={[
                { id: "live", label: "Live" },
                { id: "archive", label: "Live + Archive" },
              ]}
            />
            <TabBar
              active={statsRange}
              onChange={setStatsRange}
              tabs={[
                { id: "24h", label: "24h" },
                { id: "7d", label: "7d" },
                { id: "30d", label: "30d" },
                { id: "90d", label: "90d" },
                { id: "1y", label: "1y" },
                { id: "all", label: "All" },
              ]}
            />
          </div>
        )}
      </div>

      {/* Range-aware KPI strip — pulls from /api/stats/overview/, which
          UNIONs live Photo + PhotoArchive so the counts span photos
          deleted within the selected range. PROCESSING + ATTENTION are
          live-only by definition (archived photos can't be in those
          statuses). Hidden on Timeline (per-photo, not range-relevant). */}
      {activeTab !== "timeline" && (
      <div className="stats-summary">
        <div className="summary-card total" ref={(el) => (summaryCardsRef.current[0] = el)}>
          <div className="card-bg" />
          <div className="summary-icon">
            <HashtagIcon className="icon" />
          </div>
          <div className="summary-value">
            <AnimatedNumber value={kpiTotalImages} />
          </div>
          <div className="summary-label">Total Images</div>
        </div>
        <div className="summary-card done" ref={(el) => (summaryCardsRef.current[1] = el)}>
          <div className="card-bg" />
          <div className="summary-icon">
            <CheckCircleIcon className="icon" />
          </div>
          <div className="summary-value">
            <AnimatedNumber value={kpiCompleted} />
          </div>
          <div className="summary-label">Completed</div>
        </div>
        <div className="summary-card processing" ref={(el) => (summaryCardsRef.current[2] = el)}>
          <div className="card-bg" />
          <div className="summary-icon">
            <EllipsisHorizontalIcon className="icon" />
          </div>
          <div className="summary-value">
            <AnimatedNumber value={kpiProcessing} />
          </div>
          <div className="summary-label">Processing</div>
        </div>
        <div className="summary-card attention" ref={(el) => (summaryCardsRef.current[3] = el)}>
          <div className="card-bg" />
          <div className="summary-icon">
            <ExclamationTriangleIcon className="icon" />
          </div>
          <div className="summary-value">
            <AnimatedNumber value={kpiAttention} />
          </div>
          <div className="summary-label">Attention</div>
        </div>
        <div className="summary-card rate" ref={(el) => (summaryCardsRef.current[4] = el)}>
          <div className="card-bg" />
          <div className="summary-icon">/m</div>
          <div className="summary-value">
            {kpiImagesPerMin != null ? Number(kpiImagesPerMin).toFixed(1) : "-"}
          </div>
          <div className="summary-label">Images/min</div>
        </div>
        <div className="summary-card pipeline-time" ref={(el) => (summaryCardsRef.current[5] = el)}>
          <div className="card-bg" />
          <div className="summary-icon">ms</div>
          <div className="summary-value">
            {kpiAvgPipelineSec != null ? `${kpiAvgPipelineSec.toFixed(2)}s` : "-"}
          </div>
          <div className="summary-label">Avg Pipeline</div>
        </div>
      </div>
      )}

      {/* Editor throughput — sparkline + status mix. Both come from the
          same range-aware /api/stats/overview/ payload as the KPI strip
          so all three move together. */}
      {activeTab === "throughput" && (
        <EditorThroughputTab
          range={statsRange}
          overview={overview}
          rate={rate}
        />
      )}

      {/* Performance tab — driven by the shared statsRange. */}
      {activeTab === "pipeline" && (
        <div className="stats-content">
          <PipelinePerformance range={statsRange} includeArchived={includeArchived} />
        </div>
      )}

      {/* Timeline tab — redesigned per HANDOFF-stats-perf-timeline.md.
          Reads /api/timeline/<image_id>/. Empty state lists 5 most
          recent photos so the user lands in flow, not a blank screen. */}
      {activeTab === "timeline" && (
        <div className="stats-content timeline-content">
          <div style={{ marginBottom: 12 }}>
            <ImageSearch onSelect={setSelectedImage} />
          </div>
          <PipelineTimelineV2
            imageId={selectedImage?.image_id}
            onSelectImage={(p) =>
              setSelectedImage({ image_id: p.image_id, filename: p.filename })
            }
          />
        </div>
      )}

    </div>
  );
}
